import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import {
  DataGrid,
  GridCellParams,
  useGridApiRef,
  gridFocusCellSelector,
  gridTabIndexCellSelector,
} from '@mui/x-data-grid';
import useAgencyData from '../../networkRequest/useAgencyData';
import { downloadAgencyOfficialsReport, fetchAgencyOfficials, OfficialData } from '../../networkRequest/officialsService';
import { useUser } from '../../hooks/useUser';
import { useAgency } from '../../hooks/useAgency';
import { useRowEdit } from '../../hooks/dataGridEditing/UseRowEdit';
import { UseRowEditCalculator } from '../../hooks/dataGridEditing/UseRowEditCalculator';
import { Toast } from '@components';
import DropdownMenuButton from '@components/buttons/DropdownMenuButton';
import EmailInviteDialog from './components/EmailInviteDialog';
import EditAgencyOfficialsDialog from './components/EditAgencyOfficialsDialog';
import AddAgencyOfficialDialog from './components/AddAgencyOfficialDialog/AddAgencyOfficialDialog';
import ImportImagesDialog from '@components/ImportImagesDialog/ImportImagesDialog';
import UsersTableToolbar from './components/UsersTableToolbar';
import useColumns from './useColumns';
import { byId } from '../../utils/misc';
import Utils from '../../utils/utils';
import { getSelectedRowIds, toRowSelectionModel } from '../../utils/gridSelection';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { PAGE_PATHS } from '../../constants';
import UsersFilterBar from './components/UsersFilterBar';
import { FilterState, buildInitialFilterState, getFilterParams, FILTER_URL_KEYS, FilterKey } from './components/filterState';

const FETCH_USERS_ERROR = 'Failed to fetch users.';
const EXPORT_CSV_ERROR = 'Something went wrong. Please check your connection and try again.';
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

export type AgencyOfficialRolesMap = Map<string, number>;

const UserManagementPage: React.FC = () => {
  const { user } = useUser();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isEditingRef = useRef(false);
  const apiRef = useGridApiRef();
  // TODO: eventually we should standardize our breakpoints and 
  // avoid passing in specific pixel dimensions like this:
  const isSmallScreen = useMediaQuery('(max-width:1024px)');

  const [rows, setRows] = useState<OfficialData[]>([]);
  const [gridPagination, setGridPagination] = useState(() => ({
    page: Utils.parseIntParam(searchParams, 'page') ?? DEFAULT_PAGE,
    pageSize: Utils.parseIntParam(searchParams, 'page_size') ?? DEFAULT_PAGE_SIZE,
  }));
  const [pagination, setPagination] = useState({
    limit: 25,
    offset: 0,
    total_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(() => searchParams.get('search') ?? '');
  const [filterState, setFilterState] = useState<FilterState>(() => {
    const parseSelected = (urlKey: string): Record<string, boolean> =>
      Object.fromEntries(Utils.parseArrayParam(searchParams, urlKey).map(v => [v, true]));

    return Object.fromEntries(
      (Object.entries(FILTER_URL_KEYS) as [FilterKey, string][])
        .map(([key, urlKey]) => [key, parseSelected(urlKey)])
    ) as FilterState;
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [addOfficialDialogOpen, setAddOfficialDialogOpen] = useState<boolean>(false);
  const [isEmailInviteDialogOpen, setIsEmailInviteDialogOpen] = useState<boolean>(false);
  const [importImagesDialogOpen, setImportImagesDialogOpen] = useState<boolean>(false)
  const [selectionModel, setSelectionModel] = useState<OfficialData[]>([]);

  const fetchParams = useMemo(
    () => ({ searchText, gridPagination, filterState }),
    [searchText, gridPagination, filterState]
  );
  const debouncedParams = useDebouncedValue(fetchParams, 300);
  // Search count ref is used to ignore out-of-order responses
  const searchCountRef = useRef<number>(0);

  const {
    agencyOfficialRoles,
    error: agencyError,
    setError: setAgencyError,
  } = useAgencyData(user);

  // Edit User State
  const calculateRowEdit = UseRowEditCalculator();
  const {
    processRowUpdate,
    handleProcessRowUpdateError,
    editPromiseInfo,
    setEditPromiseInfo,
    rowMutations,
    resetRowMutations,
    error: editError
  } = useRowEdit(calculateRowEdit);

  // Store lookup for officials by id
  const officialsById = useMemo(() => byId(rows), [rows]);

  const columns = useColumns(agencyOfficialRoles);

  const errorMessages = [error, agencyError, editError];
  const hasAnyError = errorMessages.some((msg) => msg !== '');

  const clearAllErrors = () => {
    setError('');
    setAgencyError('');
  };

  const fetchUsers = useCallback(
    async () => {
      if (!agency?.id || !user?.accessToken) return;
      try {
        setLoading(true);

        // Increment the search count and save it to the ref
        const newSearchCount = searchCountRef.current + 1;
        searchCountRef.current = newSearchCount;
        const page = debouncedParams.gridPagination.page;
        const pageSize = debouncedParams.gridPagination.pageSize;
        const offset = page * pageSize;
        const limit = pageSize;

        const { data, pagination, error } = await fetchAgencyOfficials({
          agencyId: agency?.id,
          accessToken: user.accessToken,
          query: debouncedParams.searchText,
          limit,
          offset,
          ...getFilterParams(debouncedParams.filterState),
        })

        // Ignore if response is older than the current search count
        if (!data || !pagination || newSearchCount < searchCountRef.current) {
          return;
        }

        if (error) {
          setError(FETCH_USERS_ERROR);
        }

        setPagination(pagination);
        setRows(data);
      } catch {
        setError(FETCH_USERS_ERROR);
      }
      finally {
        setLoading(false);
      }
    },
    [
      agency?.id,
      user?.accessToken,
      debouncedParams
    ]
  );

  useEffect(() => {
    if (agency && location.state?.isAdmin) {
      setSuccessMessage(`Now managing ${agency.name}`)
    }
  }, [agency, location.state])

  useEffect(() => {
    fetchUsers();
  }, [agency?.id, user?.accessToken, fetchUsers, debouncedParams]);

  // MUI keeps the grid body out of the tab order until a cell is focused, so a
  // keyboard user tabs from the column headers straight to the pagination,
  // skipping every row. Point the grid's tab target at the first data cell (its
  // tabIndex only — not focus, so we don't steal focus on mount). Tab then lands
  // inside the table body, and arrow keys navigate between cells and up to the
  // headers from there.
  useEffect(() => {
    if (!apiRef.current || rows.length === 0) return;
    // Don't fight an active focus or an existing tab target that's still on screen.
    if (gridFocusCellSelector(apiRef) !== null) return;
    const tabCell = gridTabIndexCellSelector(apiRef);
    if (tabCell !== null && rows.some((row) => row.id === tabCell.id)) return;

    const firstField = columns[0]?.field;
    if (!firstField) return;
    apiRef.current.setState((state) => ({
      ...state,
      tabIndex: { ...state.tabIndex, cell: { id: rows[0].id, field: firstField } },
    }));
  }, [apiRef, rows, columns]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedParams.gridPagination.page !== DEFAULT_PAGE) {
      params.page = String(debouncedParams.gridPagination.page);
    }
    if (debouncedParams.gridPagination.pageSize !== DEFAULT_PAGE_SIZE) {
      params.page_size = String(debouncedParams.gridPagination.pageSize);
    }
    if (debouncedParams.searchText) {
      params.search = debouncedParams.searchText;
    }
    for (const [key, urlKey] of Object.entries(FILTER_URL_KEYS) as [FilterKey, string][]) {
      const values = Object.keys(debouncedParams.filterState[key]);
      if (values.length > 0) {
        params[urlKey] = values.join(',');
      }
    }
    setSearchParams(params, { replace: true });
  }, [debouncedParams, setSearchParams]);

  const onSendEmailInvites = (recipients) => {
    setSuccessMessage('Emails sent');
    setIsEmailInviteDialogOpen(false);

    recipients.forEach((updatedOfficial) => {
      saveSingleOfficialEdit({ ...updatedOfficial, invite_status: 'sent' });
    });
    setSelectionModel([]);
  };

  const saveSingleOfficialEdit = (updatedOfficial) => {
    setRows(prevData =>
      prevData.map(item =>
        item.id === updatedOfficial?.id ? updatedOfficial : item
      )
    );
  };

  const handleCellEditStart = () => {
    isEditingRef.current = true;
  };

  const handleCellEditStop = () => {
    isEditingRef.current = false;
  };

  const handleCellClick = (params: GridCellParams) => {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    if (params.field !== 'first_name' && params.field !== 'last_name') return;
    // tried to use apiRef -> getCellMode / isCellEditable but these might be reserved for payed datagrid? the types are available but their implementations are not
    // ultimately, we don't want to fire the single click when someone is currently editing.
    if (isEditingRef.current) return;

    clickTimeoutRef.current = setTimeout(() => {
      navigate(PAGE_PATHS.users + '/' + params.row.id, {
        state: {
          search: '?' + searchParams.toString(),
        },
      });
    }, 200);
  };

  const handleCellDblClick = () => {
    // we cancel the single click by clearing the timeout/current so that the navigate in handleCellClick doesn't trigger.
    // don't need to do anything else, just let MUI handle the double click.
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setGridPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleEmailInvite = useCallback(() => {
    setIsEmailInviteDialogOpen(true);
  }, []);

  const exportCsv = async () => {
    const accessToken = user?.accessToken;
    if (!agency?.id || !accessToken) return;
    try {
      await downloadAgencyOfficialsReport(accessToken, agency.id);
    } catch (error) {
      console.error('downloadAgencyOfficialsReport error', error);
      setError(EXPORT_CSV_ERROR);
    }
  };

  return (
    <Box
      sx={{
        height: isSmallScreen ? 'calc(100vh - 80px)' : 'calc(100vh - 48px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Typography component='h1' variant='xxl' >Users</Typography>
      <Grid
        container
        direction='row'
        sx={{
          justifyContent: 'flex-end',
          marginTop: 3,
          gap: 1
        }}>
        <DropdownMenuButton
          label="New user"
          onClick={() => { setAddOfficialDialogOpen(true); }}
          items={[
            { label: 'New User', onClick: () => { setAddOfficialDialogOpen(true); } },
            { label: 'Add Multiple Users', onClick: () => { navigate(PAGE_PATHS.usersImport); } },
            { label: 'Manage Photos', onClick: () => { setImportImagesDialogOpen(true); } }
          ]}
        />
      </Grid>
      <UsersFilterBar
        filterState={filterState}
        onFilterChange={setFilterState}
        onClearAll={() => setFilterState(buildInitialFilterState())}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <DataGrid
          apiRef={apiRef}
          data-testid='users'
          loading={loading}
          columns={columns}
          rows={rows}
          rowCount={pagination.total_count}
          checkboxSelection
          localeText={{ toolbarColumns: 'Manage Columns' }}
          paginationMode="server"
          paginationModel={gridPagination}
          onPaginationModelChange={setGridPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick
          keepNonExistentRowsSelected={false}
          onCellClick={handleCellClick}
          onCellDoubleClick={handleCellDblClick}
          onCellEditStart={handleCellEditStart}
          onCellEditStop={handleCellEditStop}
          getCellClassName={(params) => (params.field === 'first_name' || params.field === 'last_name' ? 'clickable-cell' : '')}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          rowSelectionModel={toRowSelectionModel(selectionModel.map((official) => official.id))}
          onRowSelectionModelChange={(newSelection) =>
            setSelectionModel(getSelectedRowIds(newSelection, apiRef).map((id) => officialsById[id]))
          }
          showToolbar
          slots={{ toolbar: UsersTableToolbar }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            toolbar: {
              searchText,
              onSearchChange: handleSearchChange,
              onEmailInvite: handleEmailInvite,
              onExport: exportCsv,
            },
          }}
          sx={{
            height: '100%',
            '& .clickable-cell:hover': {
              cursor: 'pointer'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '500'
            },
            '& .MuiDataGrid-toolbarContainer': {
              backgroundColor: '#E3F2FD'
            },
            '& .MuiDataGrid-footerContainer': {
              marginRight: '80px',
            },
          }}
        />
      </Box>
      <AddAgencyOfficialDialog
        open={addOfficialDialogOpen}
        onSuccess={() => fetchUsers()}
        onClose={() => setAddOfficialDialogOpen(false)}
        roles={agencyOfficialRoles}
        setDialogOpen={setAddOfficialDialogOpen}
      />
      <EditAgencyOfficialsDialog
        open={!!editPromiseInfo}
        editOfficialPromiseInfo={editPromiseInfo}
        setEditOfficialPromiseInfo={setEditPromiseInfo}
        rowMutations={rowMutations}
        saveSingleOfficialEdit={saveSingleOfficialEdit}
        setErrorMessage={setError}
        setSuccessMessage={setSuccessMessage}
        resetRowMutations={resetRowMutations}
        rolesMap={agencyOfficialRoles}
        onDeleteUser={() => fetchUsers()}
      />
      <EmailInviteDialog
        open={isEmailInviteDialogOpen}
        onClose={() => setIsEmailInviteDialogOpen(false)}
        onError={setError}
        onSubmit={onSendEmailInvites}
        initRecipients={selectionModel}
      />
      <ImportImagesDialog
        open={importImagesDialogOpen}
        onClose={() => setImportImagesDialogOpen(false)}
      />
      {hasAnyError && (
        <Toast type='error' onClose={clearAllErrors}>
          {errorMessages.filter((msg) => msg !== '').join('\n')}
        </Toast>
      )}
      {successMessage && (
        <Toast type='success' onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
    </Box>
  );
};

export default UserManagementPage;
